import { faCloud } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useStyles } from './MarketplaceInstallmentsTab.styles'
import { Typography } from '@mui/material'
import {
  TableComponentV2,
  NoteV2,
  NestedDropdown,
} from '@labrav/react-components'
import { useDispatch, useSelector } from '../../../../../../store'
import { useCallback, useMemo } from 'react'
import {
  MarketplaceFieldMappingInstallments,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../../../modules/marketplaceMappingManager/types'

import { updateMarketplaceTemplateSectionField } from '../../../../../../modules/marketplaceMappingManager/action'
import { parseCrmField } from '../MarketplaceTemplateDimensions/MarketplaceTemplateDimensionTable'
import { buildSalesforceCrmNestedMenu } from '../../../../utils/mappingManagerConstants'

export const MarketplaceInstallmentsTab = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const noteElement: JSX.Element = (
    <>
      If no date mapping is provided, the{' '}
      {<span className={classes.spanWords}>Offer created date</span>} will be
      used as the {<span className={classes.spanWords}>Installment date</span>}.
    </>
  )
  const tableHeaderMappings = {
    'Installment Field': 'installmentFields',
    'Salesforce Fields': 'salesforceFields',
  }
  const installmentsTableDetails = useSelector(
    state =>
      state.marketplaceMappingManager?.templateData?.templateData?.fieldMappings
        ?.installments?.installmentsMapping
  )
  const salesforceFieldsData = useSelector(
    state => state.marketplaceMappingManager.salesforceFields.fields || []
  )
  const menuDataForRow = useMemo(
    () => buildSalesforceCrmNestedMenu(salesforceFieldsData),
    [salesforceFieldsData]
  )

  function computeFieldIsMapped(
    row: MarketplaceFieldMappingInstallments,
    updates: Partial<MarketplaceFieldMappingInstallments>
  ): boolean {
    const merged: MarketplaceFieldMappingInstallments = { ...row, ...updates }

    const value = row.defaultValue ?? null
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value
    const isDefaultValue = value != null && String(value).trim() !== ''

    return !!(String(merged?.crmField ?? '') || isDefaultValue)
  }

  const installmentsTableDetailsList = useMemo(() => {
    if (!installmentsTableDetails) return []
    return Object.entries(installmentsTableDetails).map(([key, value]) => ({
      key,
      value,
    }))
  }, [installmentsTableDetails])

  const defaultSelection = useCallback(
    (installmentObj: MarketplaceFieldMappingInstallments) => {
      const crmParsed = parseCrmField(installmentObj?.crmField)
      return crmParsed && installmentObj?.crmFieldLabel
        ? [
            {
              value: crmParsed.fieldApi,
              path: `${crmParsed.objectType}.${installmentObj?.crmFieldLabel}`,
            },
          ]
        : []
    },
    []
  )

  const installmentsTableDetailsListWithMapping = useMemo(() => {
    const sortedInstallmentsTableDetailsList =
      installmentsTableDetailsList.sort(
        (a, b) => a.value.displayOrder - b.value.displayOrder
      )

    return sortedInstallmentsTableDetailsList.map(item => {
      const onSalesforceChange = (
        selectedValues: string[],
        selectedPaths: string[]
      ) => {
        const selectedValue = selectedValues[0] || ''
        const selectedPath = selectedPaths[0] || ''
        const pathParts = selectedPath.split('.')
        const crmObject = pathParts[0] || ''
        const crmFieldLabelOnly = selectedValue
          ? pathParts.slice(1).join('.') || selectedPath
          : ''

        dispatch(
          updateMarketplaceTemplateSectionField(
            'installments' as keyof MarketplaceTemplatesDataFieldMappings,
            {
              installmentsMapping: {
                ...installmentsTableDetails,
                [item.key]: {
                  ...item.value,
                  crmField:
                    crmObject && selectedValue
                      ? `${crmObject}.${selectedValue}`
                      : selectedValue,
                  crmFieldLabel: crmFieldLabelOnly,
                  isMapped: computeFieldIsMapped(item.value, {
                    crmField:
                      crmObject && selectedValue
                        ? `${crmObject}.${selectedValue}`
                        : selectedValue,
                    crmFieldLabel: crmFieldLabelOnly,
                  }),
                },
              },
            } as Partial<MarketplaceFieldMappingInstallments>
          )
        )
      }

      return {
        installmentFields: (
          <Typography className={classes.installmentField}>
            {item.value.label}
          </Typography>
        ),
        salesforceFields: (
          <div>
            <NestedDropdown
              data-testid={`salesforce-field-dropdown-${item.value.label}`}
              menuData={menuDataForRow}
              placeholder="Select an option"
              width="381px"
              height="44px"
              allowMultiple={false}
              defaultSelectedItems={defaultSelection(item.value)}
              onSelectionChange={onSalesforceChange}
            />
          </div>
        ),
      }
    })
  }, [
    installmentsTableDetailsList,
    tableHeaderMappings,
    installmentsTableDetails,
  ])

  const itemHeight = installmentsTableDetailsList.length * 64

  const TABLE_CELL_PROPS = [
    { style: { height: '32px' } },
    { style: { height: '32px' } },
  ]

  return (
    <>
      <div
        data-testid="marketplace-installments-tab"
        className={classes.tabWrapper}
      >
        <div className={classes.headingContainer}>
          <div className={classes.iconWrapper}>
            <FontAwesomeIcon icon={faCloud} className={classes.icon} />
          </div>
          <Typography className={classes.heading}>Salesforce Object</Typography>
        </div>
        <div>
          <NoteV2 description={noteElement} />
          <div style={{ width: '70%' }}>
            <TableComponentV2
              keyName={'Installment mapping'}
              objects={installmentsTableDetailsListWithMapping}
              mapping={tableHeaderMappings}
              fetchMoreData={() => {}}
              tableHeight={`calc(100% - ${itemHeight}px)`}
              selectedItem={[]}
              onChangeCheckbox={() => {}}
              showCheckbox={false}
              breakWorkHeader={false}
              hasMoreData={false}
              customCellProps={TABLE_CELL_PROPS}
              removeBasicTablePadding
              removeGap
            />
          </div>
        </div>
      </div>
    </>
  )
}
